import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const OFFICE_PREVIEW_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx'];
const IMAGE_PREVIEW_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const DOCX_PAGE_WIDTH = 816;

/** Read-only Office embed (no ribbon). view.aspx shows the full editor chrome. */
export const getOfficePreviewEmbedUrl = (fileUrl: string): string => {
  const params = new URLSearchParams({
    src: fileUrl,
    wdPrint: '0',
    wdEmbedCode: '0',
  });
  return `https://view.officeapps.live.com/op/embed.aspx?${params}`;
};

const safeDecodeURIComponent = (value = ''): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getFileExtension = (filename = '', fileUrl = ''): string => {
  const cleanName = filename.split('?')[0].split('#')[0];
  if (cleanName.includes('.')) {
    return cleanName.split('.').pop()?.toLowerCase() ?? '';
  }

  const decodedUrl = safeDecodeURIComponent(fileUrl).split('?')[0].split('#')[0];
  if (decodedUrl.includes('.')) {
    return decodedUrl.split('.').pop()?.toLowerCase() ?? '';
  }

  return '';
};

/** Symbol/Wingdings bullet glyphs from docx-preview do not render reliably on macOS. */
const fixDocxBulletStyles = (container: HTMLElement): void => {
  container.querySelectorAll('style').forEach((styleEl) => {
    styleEl.textContent = (styleEl.textContent ?? '').replace(
      /(p\.attachment-docx-content-num-[\w-]+:before\s*\{)([^}]*)\}/gi,
      (match, opener, body) => {
        if (/content:\s*"[^"]*counter\(/i.test(body) || !/counter-increment/i.test(body)) {
          return match;
        }

        const fixedBody = body
          .replace(/font-family:\s*[^;]+;/gi, '')
          .replace(
            /content:\s*"[^"]*";/i,
            'content: "\\2022\\00a0"; font-family: Arial, Helvetica, sans-serif; font-weight: 800;'
          );

        return `${opener}${fixedBody}}`;
      }
    );
  });
};

interface AttachmentNavButtonProps {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}

const AttachmentNavButton = ({ direction, disabled, onClick }: AttachmentNavButtonProps) => (
  <button
    type='button'
    className={`general-button ${direction === 'prev' ? 'total-contributions-prev' : 'total-contributions-next'}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
    style={{ width: '22px', height: '22px' }}
  >
    <span className={direction === 'prev' ? 'icon-arrow-back' : 'icon-arrow-next'} />
  </button>
);

interface SharePreviewContentProps {
  url: string;
  filename?: string;
  containedPreview?: boolean;
}

const PDF_ZOOM_MIN = 0.5;
const PDF_ZOOM_MAX = 2;
const PDF_ZOOM_STEP = 0.1;

const SharePreviewContent = ({ url, filename, containedPreview = false }: SharePreviewContentProps) => {
  const extension = useMemo(() => getFileExtension(filename, url), [filename, url]);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfContainerWidth, setPdfContainerWidth] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const pdfScrollContainerRef = useRef<HTMLDivElement>(null);
  const pdfPageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState('');
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const docxPreviewRef = useRef<HTMLDivElement>(null);
  const [docxContainerWidth, setDocxContainerWidth] = useState<number | null>(null);
  const docxScale = useMemo(() => {
    if (!docxContainerWidth || docxContainerWidth >= DOCX_PAGE_WIDTH) {
      return 1;
    }

    return docxContainerWidth / DOCX_PAGE_WIDTH;
  }, [docxContainerWidth]);

  useEffect(() => {
    setPdfPage(1);
    setPdfPages(0);
    setPdfZoom(1);
    setDocxContainerWidth(null);
    pdfPageRefs.current = {};
  }, [url]);

  useLayoutEffect(() => {
    if (extension !== 'pdf') {
      return;
    }

    const updateWidth = () => {
      const width = pdfScrollContainerRef.current?.clientWidth;

      if (width && width > 0) {
        setPdfContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = pdfScrollContainerRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [extension, url]);

  useEffect(() => {
    if (extension !== 'pdf' || pdfPages === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        const page = Number(mostVisible?.target.getAttribute('data-page-number'));

        if (page) {
          setPdfPage(page);
        }
      },
      { threshold: [0.5] }
    );

    Object.values(pdfPageRefs.current).forEach((page) => page && observer.observe(page));

    return () => observer.disconnect();
  }, [extension, pdfPages]);

  const scrollToPdfPage = (page: number) => {
    setPdfPage(page);
    pdfPageRefs.current[page]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** Keeps the point currently at the viewport's centre stationary as the zoom changes, so the page grows outward from where the user is looking instead of jumping to the top-left. */
  const changeZoom = (nextZoom: number) => {
    const clampedZoom = Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, +nextZoom.toFixed(2)));
    const container = pdfScrollContainerRef.current;

    if (container && clampedZoom !== pdfZoom) {
      const ratio = clampedZoom / pdfZoom;
      const targetScrollLeft = (container.scrollLeft + container.clientWidth / 2) * ratio - container.clientWidth / 2;
      const targetScrollTop = (container.scrollTop + container.clientHeight / 2) * ratio - container.clientHeight / 2;

      requestAnimationFrame(() => {
        container.scrollLeft = targetScrollLeft;
        container.scrollTop = targetScrollTop;
      });
    }

    setPdfZoom(clampedZoom);
  };

  const handleDownload = async () => {
    if (!url || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download document. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobURL;
      anchor.style.display = 'none';

      anchor.download = filename?.length ? filename : 'document';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(blobURL), 0);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadButton = (
    <button
      type='button'
      className='general-button d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 rounded-pill hovered-button bg-white paragraph-p3 share-preview-controls-download'
      disabled={isDownloading}
      onClick={handleDownload}
    >
      {isDownloading ? (
        <i className='fas fa-spinner fa-spin' />
      ) : (
        <>
          <span className='icon-download-attachment mr-2' />
          Download
        </>
      )}
    </button>
  );

  const renderPdfControlsBar = (left: React.ReactNode, right: React.ReactNode) => (
    <div className='px-3 py-2 border-general specific-bottom-margin-12 border-grey-50 rounded bg-grey share-preview-controls share-preview-controls-grid'>
      <div className='share-preview-controls-zoom'>{left}</div>
      {downloadButton}
      <div className='share-preview-controls-pagination'>{right}</div>
    </div>
  );

  const renderDownloadBar = () => (
    <div className='d-flex justify-content-end px-3 py-2 border-general specific-bottom-margin-12 border-grey-50 rounded bg-grey share-preview-controls'>
      {downloadButton}
    </div>
  );

  useLayoutEffect(() => {
    if (extension !== 'docx' || !containedPreview) {
      return;
    }

    const updateWidth = () => {
      const width = docxPreviewRef.current?.clientWidth;

      if (width && width > 0) {
        setDocxContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = docxPreviewRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [containedPreview, docxLoading, extension, url]);

  useLayoutEffect(() => {
    const container = docxContainerRef.current;

    if (extension !== 'docx' || !containedPreview || docxLoading || !container) {
      return;
    }

    const resetWrapperStyles = (wrapper: HTMLElement) => {
      wrapper.style.transform = '';
      wrapper.style.transformOrigin = '';
      wrapper.style.width = '';
    };

    const unwrapScaleFrame = () => {
      const scaleFrame = container.querySelector<HTMLElement>(':scope > .attachment-docx-scale-frame');

      if (!scaleFrame) {
        return;
      }

      const contentWrapper = scaleFrame.querySelector<HTMLElement>('.attachment-docx-content-wrapper');

      if (contentWrapper) {
        resetWrapperStyles(contentWrapper);
        container.insertBefore(contentWrapper, scaleFrame);
      }

      scaleFrame.remove();
    };

    if (docxScale >= 1) {
      unwrapScaleFrame();
      return;
    }

    let contentWrapper = container.querySelector<HTMLElement>(':scope > .attachment-docx-content-wrapper');

    if (!contentWrapper) {
      contentWrapper = container.querySelector<HTMLElement>(
        '.attachment-docx-scale-frame > .attachment-docx-content-wrapper'
      );
    }

    if (!contentWrapper) {
      return;
    }

    let scaleFrame = container.querySelector<HTMLElement>(':scope > .attachment-docx-scale-frame');

    if (!scaleFrame) {
      scaleFrame = document.createElement('div');
      scaleFrame.className = 'attachment-docx-scale-frame';
      container.appendChild(scaleFrame);
      scaleFrame.appendChild(contentWrapper);
    } else if (contentWrapper.parentElement !== scaleFrame) {
      scaleFrame.appendChild(contentWrapper);
    }

    const naturalHeight = contentWrapper.scrollHeight;
    scaleFrame.style.width = `${DOCX_PAGE_WIDTH * docxScale}px`;
    scaleFrame.style.height = `${naturalHeight * docxScale}px`;
  }, [containedPreview, docxContainerWidth, docxLoading, docxScale, extension, url]);

  useEffect(() => {
    if (extension !== 'docx' || !docxContainerRef.current) return;

    let cancelled = false;
    const currentContainer = docxContainerRef.current;
    currentContainer.innerHTML = '';
    setDocxError('');
    setDocxLoading(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load DOCX file');
        }
        return response.blob();
      })
      .then((blob) =>
        renderAsync(blob, currentContainer, undefined, {
          className: 'attachment-docx-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
        })
      )
      .then(() => {
        if (!cancelled) {
          fixDocxBulletStyles(currentContainer);
          setDocxLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocxError('Unable to preview this DOCX file.');
          setDocxLoading(false);
        }
      });

    return () => {
      cancelled = true;
      currentContainer.innerHTML = '';
    };
  }, [extension, url]);

  if (extension === 'pdf') {
    const zoomControls = pdfPages > 0 && (
      <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
        <button
          type='button'
          className='general-button d-flex align-items-center justify-content-center paragraph-p3'
          disabled={pdfZoom <= PDF_ZOOM_MIN}
          onClick={() => changeZoom(pdfZoom - PDF_ZOOM_STEP)}
          aria-label='Zoom out'
          style={{ width: '22px', height: '22px' }}
        >
          −
        </button>
        <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>{Math.round(pdfZoom * 100)}%</span>
        <button
          type='button'
          className='general-button d-flex align-items-center justify-content-center paragraph-p3'
          disabled={pdfZoom >= PDF_ZOOM_MAX}
          onClick={() => changeZoom(pdfZoom + PDF_ZOOM_STEP)}
          aria-label='Zoom in'
          style={{ width: '22px', height: '22px' }}
        >
          +
        </button>
      </div>
    );

    const paginationControls = pdfPages > 0 && (
      <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
        <AttachmentNavButton direction='prev' disabled={pdfPage <= 1} onClick={() => scrollToPdfPage(pdfPage - 1)} />
        <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>
          Page {pdfPage} of {pdfPages}
        </span>
        <AttachmentNavButton
          direction='next'
          disabled={pdfPage >= pdfPages}
          onClick={() => scrollToPdfPage(pdfPage + 1)}
        />
      </div>
    );

    return (
      <>
        {renderPdfControlsBar(zoomControls, paginationControls)}
        <div className='overflow-auto attachment-pdf-fill-preview rounded border' ref={pdfScrollContainerRef}>
          <Document
            file={url}
            onLoadSuccess={({ numPages }) => {
              setPdfPages(numPages);
              setPdfPage((prev) => Math.min(prev, numPages || 1));
            }}
            loading={<p className='paragraph-general mb-0 p-3'>Loading PDF...</p>}
            error={<p className='paragraph-general mb-0 p-3'>Unable to preview this PDF file.</p>}
          >
            {Array.from({ length: pdfPages }, (_, index) => {
              const pageNumber = index + 1;

              return (
                <div
                  key={`page-${pageNumber}`}
                  ref={(el) => {
                    pdfPageRefs.current[pageNumber] = el;
                  }}
                  data-page-number={pageNumber}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={pdfContainerWidth ? pdfContainerWidth * pdfZoom : undefined}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    className='attachment-pdf-page'
                  />
                </div>
              );
            })}
          </Document>
        </div>
      </>
    );
  }

  if (extension === 'docx') {
    const shouldScaleDocx = containedPreview && docxScale < 1;
    const docxRenderContainer = (
      <div
        ref={docxContainerRef}
        className={`attachment-docx-wrapper${containedPreview ? ' attachment-docx-wrapper-contained' : ''}${
          shouldScaleDocx ? ' attachment-docx-wrapper-scaled' : ''
        }`}
        style={
          {
            ...(shouldScaleDocx ? { '--docx-scale': docxScale } : {}),
          } as React.CSSProperties
        }
      />
    );

    if (containedPreview) {
      return (
        <div>
          {renderDownloadBar()}
          <div ref={docxPreviewRef} className='attachment-preview-height-container'>
            {docxLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
            {docxError && <p className='paragraph-general mb-3'>{docxError}</p>}
            {docxRenderContainer}
          </div>
        </div>
      );
    }

    return (
      <div>
        {renderDownloadBar()}
        <div className='p-3 border rounded'>
          {docxLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
          {docxError && <p className='paragraph-general mb-3'>{docxError}</p>}
          {docxRenderContainer}
        </div>
      </div>
    );
  }

  if (IMAGE_PREVIEW_EXTENSIONS.includes(extension)) {
    const image = <img src={url} alt={filename} className='attachment-preview-image' />;

    if (containedPreview) {
      return (
        <div>
          {renderDownloadBar()}
          <div className='attachment-preview-height-container'>{image}</div>
        </div>
      );
    }

    return (
      <div>
        {renderDownloadBar()}
        <div className='p-3 border rounded'>{image}</div>
      </div>
    );
  }

  if (OFFICE_PREVIEW_EXTENSIONS.includes(extension)) {
    const officePreview = (
      <iframe
        src={getOfficePreviewEmbedUrl(url)}
        title={`preview-${filename}`}
        className={`attachment-preview-iframe${containedPreview ? ' attachment-preview-iframe-contained' : ''}`}
        loading='lazy'
        referrerPolicy='no-referrer'
      />
    );

    if (containedPreview) {
      return (
        <div>
          {renderDownloadBar()}
          <div className='attachment-preview-height-container'>{officePreview}</div>
        </div>
      );
    }

    return (
      <div>
        {renderDownloadBar()}
        {officePreview}
      </div>
    );
  }

  return (
    <div>
      {renderDownloadBar()}
      <p className='paragraph-general mb-0'>Preview is not available for this file type.</p>
    </div>
  );
};

export default SharePreviewContent;
